import { openLink } from '@/shared/telegram';

import { AppError, isAppError } from '@/shared/api';

export type ToastType = 'error' | 'success' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastEvent {
  message: string;
  type: ToastType;
  durationMs?: number;
  action?: ToastAction;
}

type ToastListener = (event: ToastEvent) => void;

const listeners = new Set<ToastListener>();

export function subscribeToToast(listener: ToastListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function emitToast(event: ToastEvent): void {
  listeners.forEach((listener) => listener(event));
}

export const SUPPORT_LINK = 'https://t.me/shanti_app';

export function createSupportToastAction(label = 'Support'): ToastAction {
  return {
    label,
    onClick: () => openLink(SUPPORT_LINK),
  };
}

export function createReloadAction(): ToastAction {
  return {
    label: 'Refresh',
    onClick: () => {
      window.location.reload();
    },
  };
}

type EmitErrorOptions = {
  durationMs?: number;
  action?: ToastAction;
  message: string;
};

const AUTH_TOAST_MESSAGE = 'Error. Please reload application or contact support.';
const OFFLINE_TOAST_MESSAGE = "Looks like you're offline. Try reloading the application.";
const TIMEOUT_TOAST_MESSAGE = 'Request timed out. Try reloading the application.';

type ErrorCategory = 'auth' | 'offline' | 'timeout' | 'generic';

const classifyError = (error: unknown): ErrorCategory => {
  const isNavigatorOffline = typeof navigator !== 'undefined' && navigator.onLine === false;

  if (isAppError(error)) {
    if (error.kind === 'auth') return 'auth';
    if (error.kind === 'network') return 'offline';
    if (error.kind === 'timeout') return 'timeout';
  }
  if (isNavigatorOffline) return 'offline';
  return 'generic';
};

const ERROR_DEDUPE_TTL_MS = 1500;
const recentErrorFingerprints = new Map<string, number>();

const fingerprintForCategory = (
  category: ErrorCategory,
  error: unknown,
  message: string
): string => {
  if (category === 'auth') return `cat:auth:${AUTH_TOAST_MESSAGE}`;
  if (category === 'offline') return `cat:offline:${OFFLINE_TOAST_MESSAGE}`;
  if (category === 'timeout') return `cat:timeout:${TIMEOUT_TOAST_MESSAGE}`;

  if (error instanceof AppError) {
    return `app:${error.kind}:${error.status ?? ''}:${error.message}:${message}`;
  }
  if (error instanceof Error) {
    return `err:${error.name}:${error.message}:${message}`;
  }
  if (typeof error === 'string') {
    return `str:${error}:${message}`;
  }
  return `unknown:${message}`;
};

const shouldEmit = (fingerprint: string): boolean => {
  const now = Date.now();
  for (const [key, ts] of recentErrorFingerprints) {
    if (now - ts > ERROR_DEDUPE_TTL_MS) {
      recentErrorFingerprints.delete(key);
    }
  }

  const lastTs = recentErrorFingerprints.get(fingerprint);
  if (lastTs !== undefined && now - lastTs <= ERROR_DEDUPE_TTL_MS) {
    return false;
  }

  recentErrorFingerprints.set(fingerprint, now);
  return true;
};

export function emitErrorToast(error: unknown, options: EmitErrorOptions): void {
  const category = classifyError(error);
  const fingerprint = fingerprintForCategory(category, error, options.message);

  if (!shouldEmit(fingerprint)) return;

  if (category === 'auth') {
    emitToast({
      message: AUTH_TOAST_MESSAGE,
      type: 'error',
      action: options.action ?? createReloadAction(),
      durationMs: options.durationMs,
    });
    return;
  }

  if (category === 'offline') {
    emitToast({
      message: OFFLINE_TOAST_MESSAGE,
      type: 'error',
      action: options.action ?? createReloadAction(),
      durationMs: options.durationMs,
    });
    return;
  }

  if (category === 'timeout') {
    emitToast({
      message: TIMEOUT_TOAST_MESSAGE,
      type: 'error',
      action: options.action ?? createReloadAction(),
      durationMs: options.durationMs,
    });
    return;
  }

  if (options.message) {
    emitToast({
      message: options.message,
      type: 'error',
      action: options.action ?? createSupportToastAction(),
      durationMs: options.durationMs,
    });
  }
}

export function __resetErrorToastDedupeForTests(): void {
  recentErrorFingerprints.clear();
}
